import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, type TenantDetail } from '../lib/api';

/** Rendered on the tenant Overview tab when Microsoft rejected the
 * tenant's sign-in credentials during reset_admin_pw. Two reasons,
 * each verified against the live MS login flow via Playwright probe:
 *
 *   - `invalid_username` — the tenant login (ga_upn) doesn't exist.
 *     MS surfaces "We couldn't find an account with that username."
 *     The operator updates both ga_upn and ga_password.
 *   - `invalid_password` — the password is wrong. MS surfaces "Your
 *     account or password is incorrect." Operator updates ga_password.
 *
 * Submitting hits POST /api/tenants/{id}/credentials which persists
 * the new values, clears `state_blob.signin_failure`, deletes the
 * stale reset_admin_pw PhaseRun rows (so the next pipeline run
 * actually re-attempts sign-in instead of short-circuiting on
 * "already done"), and re-enqueues bootstrap.
 */
export default function SigninFailureCard({ tenant }: { tenant: TenantDetail }) {
  const qc = useQueryClient();
  const failure = tenant.signin_failure;
  const [gaUpn, setGaUpn] = useState(tenant.ga_upn);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!failure) return null;

  const isBadUsername = failure.reason === 'invalid_username';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      await api.updateCredentials(tenant.id, {
        ga_password: password,
        ga_upn: isBadUsername ? gaUpn : undefined,
      });
      qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
      qc.invalidateQueries({ queryKey: ['tenants'] });
      setPassword('');
    } catch (e: any) {
      setErr(e?.message || 'update failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card p-5 border border-err/30">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-err">
          {isBadUsername ? 'Tenant login not found' : 'Credentials rejected'}
        </div>
        <span className="text-[11px] text-dim">
          Microsoft 365 sign-in
        </span>
      </div>
      <div className="text-sm text-ink mb-3">
        Microsoft rejected the credentials Sendepot tried for{' '}
        <span className="font-mono">{tenant.ga_upn}</span>. Provisioning is
        paused until working credentials are submitted.
      </div>
      <div className="text-[12px] text-dim italic mb-4">
        “{failure.message}”
      </div>
      <form onSubmit={handleSubmit} className="space-y-3">
        {isBadUsername && (
          <div>
            <label className="block text-xs text-dim mb-1">Tenant login (UPN)</label>
            <input
              type="email"
              required
              value={gaUpn}
              onChange={(e) => setGaUpn(e.target.value)}
              className="w-full bg-white/[0.04] border border-line rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-accent"
              placeholder="admin@yourtenant.onmicrosoft.com"
            />
          </div>
        )}
        <div>
          <label className="block text-xs text-dim mb-1">Password</label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-white/[0.04] border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent"
            placeholder="Microsoft 365 admin password"
            autoComplete="new-password"
          />
        </div>
        <div className="flex items-center gap-2">
          <button type="submit" disabled={busy || !password} className="btn-primary disabled:opacity-50">
            {busy ? 'Updating…' : 'Update & retry'}
          </button>
          <span className="text-[11px] text-dim">
            The pipeline will retry sign-in automatically after submission.
          </span>
        </div>
        {err && <div className="text-xs text-err">{err}</div>}
      </form>
    </div>
  );
}
